import { TCommunicationsCall } from "./api-communications";

export type TCallSessionState =
  | "idle"
  | "preparing_media"
  | "registering_client"
  | "outbound_creating"
  | "outbound_ringing"
  | "incoming_ringing"
  | "connecting_media"
  | "connected"
  | "ending"
  | "ended"
  | "error";

const ACTIVE_CALL_STATUSES = new Set([
  "queued",
  "initiated",
  "ringing",
  "in-progress",
  "in_progress",
  "answered",
]);

const TERMINAL_CALL_STATUSES = new Set([
  "completed",
  "failed",
  "busy",
  "no_answer",
  "no-answer",
  "canceled",
  "cancelled",
]);

const PENDING_HANGUP_STATUSES = new Set(["pending", "provider_accepted"]);

export const normalizeCallStatus = (
  status: string | null | undefined,
): string => (status || "").trim().toLowerCase();

export const isCallActive = (status: string | null | undefined): boolean => {
  return ACTIVE_CALL_STATUSES.has(normalizeCallStatus(status));
};

export const isCallTerminal = (status: string | null | undefined): boolean => {
  return TERMINAL_CALL_STATUSES.has(normalizeCallStatus(status));
};

export const shouldPollCall = (call: TCommunicationsCall | null): boolean => {
  if (!call || isCallTerminal(call.status)) {
    return false;
  }

  if (isCallActive(call.status)) {
    return true;
  }

  const normalizedHangupStatus = (call.hangup_request_status || "")
    .trim()
    .toLowerCase();
  return PENDING_HANGUP_STATUSES.has(normalizedHangupStatus);
};

export const mapCallToSessionState = (
  currentState: TCallSessionState,
  call: TCommunicationsCall,
): TCallSessionState => {
  if (isCallTerminal(call.status)) {
    return "ended";
  }

  if (currentState === "ending") {
    return "ending";
  }

  const normalizedStatus = normalizeCallStatus(call.status);
  if (normalizedStatus === "ringing") {
    if (currentState === "incoming_ringing") {
      return "incoming_ringing";
    }
    return "outbound_ringing";
  }

  if (["in-progress", "in_progress", "answered"].includes(normalizedStatus)) {
    return "connected";
  }

  if (["queued", "initiated"].includes(normalizedStatus)) {
    return "outbound_creating";
  }

  return currentState;
};

export const describeSessionState = (state: TCallSessionState): string => {
  switch (state) {
    case "preparing_media":
      return "Requesting microphone access";
    case "registering_client":
      return "Registering browser audio leg";
    case "outbound_creating":
      return "Creating outbound call";
    case "outbound_ringing":
      return "Ringing patient";
    case "incoming_ringing":
      return "Incoming call ringing";
    case "connecting_media":
      return "Connecting audio";
    case "connected":
      return "Browser call active";
    case "ending":
      return "Hangup requested";
    case "ended":
      return "Call ended";
    case "error":
      return "Call failed";
    default:
      return "Ready";
  }
};

export const getReadableTerminalReason = (
  statusReason: string | null | undefined,
): string | null => {
  if (!statusReason) {
    return null;
  }
  return statusReason.replace(/_/g, " ").trim();
};
